// A filter on one column of a list: the filter icon on a column you can sort on (Camiel,
// 21-09-2026: "zodat je op die ene kolom specifiek kunt filteren op iets"), and since
// 22-09-2026 on every column ("alle kolommen moeten filterbaar zijn"). One value per
// column, written the way the API reads it (docs/api.md §9.19), so a paged list sends it
// as it is and a list held in the browser tests its rows against the same thing:
//
//   text     "jansen"                  contains, case and accents aside
//   options  "active,inactive"         one of these
//   date     "2026-09-01..2026-09-30"  from and to, both days included; either side may be empty
//   number   "10..20"                  from and to, both included; either side may be empty

export interface FilterOption {
  value: string;
  /** A translation key; or `label` when the text comes from data (a mailbox, a category). */
  labelKey?: string;
  label?: string;
}

export type ColumnFilterDef =
  | { kind: "text" }
  | { kind: "options"; options: readonly FilterOption[] }
  | { kind: "date" }
  /** `unitKey`: what the numbers count when the cells say it otherwise — hours for "5 u 30". */
  | { kind: "number"; unitKey?: string };

/** Per column key, the value as written above; a column without a filter is not in it. */
export type FilterValues = Readonly<Record<string, string>>;

const PREFIX = "filter.";

/** The column filters a list's search can carry: filter.<key>, beside its own keys. */
export type FilterSearch = { [key: `filter.${string}`]: string | undefined };

/** The column filters in a list's address bar. */
export function filtersOf(search: object): FilterValues {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(search)) {
    if (key.startsWith(PREFIX) && typeof value === "string" && value.trim() !== "") out[key.slice(PREFIX.length)] = value;
  }
  return out;
}

/** The same, as query parameters for the API. */
export function filterParams(values: FilterValues): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(values)) out[`${PREFIX}${key}`] = value;
  return out;
}

/** The column filters of a route's search, for its validateSearch: kept as they are. */
export function filterSearch(search: object): FilterSearch {
  return filterParams(filtersOf(search)) as FilterSearch;
}

/** The address-bar key of one column's filter. */
export function filterParam(key: string): string {
  return `${PREFIX}${key}`;
}

/** "a..b" as its two sides; a single value is both. */
export function rangeOf(value: string | undefined): [string, string] {
  if (!value) return ["", ""];
  if (!value.includes("..")) return [value, value];
  const [from = "", to = ""] = value.split("..");
  return [from.trim(), to.trim()];
}

/** Two sides as one value; both empty is no filter. */
export function rangeValue(from: string, to: string): string {
  const a = from.trim();
  const b = to.trim();
  if (!a && !b) return "";
  if (a && a === b) return a;
  return `${a}..${b}`;
}

/** The chosen values of an options filter. */
export function optionsOf(value: string | undefined): string[] {
  return value
    ? value
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean)
    : [];
}

// ─── A list held in the browser ─────────────────────────────────────────────

export type FilterReading = string | number | boolean | null | undefined;

export function folded(text: string): string {
  return text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function numberOf(text: string): number | null {
  if (text === "") return null;
  const value = Number(text.replace(",", "."));
  return Number.isFinite(value) ? value : null;
}

/**
 * Whether a reading passes a filter of this kind. A yes/no reading counts as "yes" or
 * "no", and for the status column as "active" or "inactive" — what the options say.
 */
export function passes(kind: ColumnFilterDef["kind"], filter: string, reading: FilterReading | FilterReading[]): boolean {
  const readings = Array.isArray(reading) ? reading : [reading];
  switch (kind) {
    case "text": {
      const needle = folded(filter.trim());
      return readings.some((value) => value !== null && value !== undefined && folded(String(value)).includes(needle));
    }
    case "options": {
      const chosen = optionsOf(filter);
      return readings.some((value) => {
        if (value === null || value === undefined) return false;
        if (typeof value === "boolean") return chosen.includes(value ? "yes" : "no") || chosen.includes(value ? "active" : "inactive");
        return chosen.includes(String(value));
      });
    }
    case "number": {
      const [fromText, toText] = rangeOf(filter);
      const min = numberOf(fromText);
      const max = numberOf(toText);
      return readings.some((value) => {
        const number = typeof value === "number" ? value : typeof value === "string" ? numberOf(value) : null;
        return number !== null && (min === null || number >= min) && (max === null || number <= max);
      });
    }
    case "date": {
      const [from, to] = rangeOf(filter);
      return readings.some((value) => {
        if (typeof value !== "string" || value === "") return false;
        const day = value.length > 10 ? amsterdamDay(value) : value;
        return (!from || day >= from) && (!to || day <= to);
      });
    }
  }
}

function amsterdamDay(iso: string): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Amsterdam", year: "numeric", month: "2-digit", day: "2-digit" }).format(
    new Date(iso),
  );
}

/**
 * The rows that pass every filter, read with `readers` — a list that is loaded whole
 * filters the way the paged lists do on the server. A filter without a reader, or of a
 * column that has no filter, leaves the rows as they are.
 */
export function filterRows<T>(
  rows: readonly T[],
  values: FilterValues,
  defs: Readonly<Record<string, ColumnFilterDef["kind"]>>,
  readers: Readonly<Record<string, (row: T) => FilterReading | FilterReading[]>>,
): T[] {
  const active = Object.entries(values).filter(([key]) => defs[key] && readers[key]);
  if (active.length === 0) return [...rows];
  return rows.filter((row) => active.every(([key, value]) => passes(defs[key]!, value, readers[key]!(row))));
}

// ─── What most columns filter with ──────────────────────────────────────────

export const TEXT_FILTER: ColumnFilterDef = { kind: "text" };
export const DATE_FILTER: ColumnFilterDef = { kind: "date" };
export const NUMBER_FILTER: ColumnFilterDef = { kind: "number" };

/** The status column every list has. */
export const ACTIVE_FILTER: ColumnFilterDef = {
  kind: "options",
  options: [
    { value: "active", labelKey: "common.active" },
    { value: "inactive", labelKey: "common.inactive" },
  ],
};

/** A column that is a checkbox. */
export const YES_NO_FILTER: ColumnFilterDef = {
  kind: "options",
  options: [
    { value: "yes", labelKey: "common.yes" },
    { value: "no", labelKey: "common.no" },
  ],
};

/** A choice from a map of values to label keys, in the map's order. */
export function optionsFilter(labelKeys: Readonly<Record<string, string>>): ColumnFilterDef {
  return { kind: "options", options: Object.entries(labelKeys).map(([value, labelKey]) => ({ value, labelKey })) };
}
